import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "../../components/Layout";
import { Filter, Search, Check, X, RotateCcw } from "lucide-react";
import "./../../styles/member/history.css";
import {
  TransactionsService,
  TransactionDto,
} from "../../services/transactionsService";
import { useAuth } from "../../context/AuthContext";
import { AuthService } from "../../services/auth";
import { formatDateTimeWithSeconds } from "../../utils/dateFormatter";

const History: React.FC = () => {
  const { login } = useAuth();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [transactions, setTransactions] = useState<TransactionDto[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionDto | null>(null);
  const [actionType, setActionType] = useState<
    "approve" | "reject" | "cancel" | null
  >(null);
  const [rejectReason, setRejectReason] = useState<string>("");

  const [typeOpen, setTypeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const typeOptions = [
    { value: "", label: "Loại giao dịch" },
    { value: "deposit", label: "Nạp tiền" },
    { value: "payment", label: "Thanh toán" },
    { value: "refund", label: "Hoàn tiền" },
    { value: "commission", label: "Hoa hồng" },
    { value: "withdraw", label: "Rút tiền" },
    { value: "transfer", label: "Chuyển khoản" },
  ];

  const statusOptions = [
    { value: "", label: "Trạng thái" },
    { value: "completed", label: "Hoàn thành" },
    { value: "pending", label: "Đang xử lí" },
    { value: "failed", label: "Thất bại" },
    { value: "cancelled", label: "Đã hủy" },
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await TransactionsService.listAll({
          page,
          limit,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
        });
        if (!mounted) return;
        setTransactions(res.data || []);
        setTotalPages(res.totalPages || 1);
      } catch (e: any) {
        if (mounted)
          setError(e?.message || "Không thể tải danh sách giao dịch");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [page, limit, statusFilter, typeFilter]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return (transactions || []).filter(
      (t) =>
        (t.transactionCode || "").toLowerCase().includes(q) ||
        String(t.userId).includes(q) ||
        (t.user?.email || "").toLowerCase().includes(q) ||
        (t.user?.fullName || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.type || "").toLowerCase().includes(q) ||
        (t.status || "").toLowerCase().includes(q)
    );
  }, [transactions, searchQuery]);

  const handleAction = (
    transaction: TransactionDto,
    action: "approve" | "reject" | "cancel"
  ) => {
    setSelectedTransaction(transaction);
    setActionType(action);
    setRejectReason("");
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!selectedTransaction || !actionType) return;

    try {
      setActionLoading(selectedTransaction.id);
      let updatedTransaction: TransactionDto;

      switch (actionType) {
        case "approve":
          updatedTransaction = await TransactionsService.confirmTransaction(
            selectedTransaction.transactionCode
          );
          break;
        case "reject":
          updatedTransaction = await TransactionsService.rejectTransaction(
            selectedTransaction.transactionCode,
            rejectReason
          );
          break;
        case "cancel":
          updatedTransaction = await TransactionsService.cancelTransaction(
            selectedTransaction.transactionCode
          );
          break;
        default:
          return;
      }

      // Cập nhật transaction trong danh sách
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === selectedTransaction.id ? updatedTransaction : t
        )
      );

      // Refresh user data sau khi cập nhật giao dịch để cập nhật role
      try {
        // Force refresh bằng cách thêm timestamp để bypass cache
        const currentUser = await AuthService.getCurrentUser();

        // Ánh xạ người dùng LoginResponse tới Người dùng AuthContext
        const mappedUser = {
          id: currentUser.id,
          email: currentUser.email,
          fullName: currentUser.fullName,
          isActive: currentUser.isActive,
          role: currentUser.role as any,
        };

        // Buộc cập nhật AuthContext
        login(mappedUser);

        // Buộc cập nhật localStorage để đồng bộ hóa trên các tab
        const authData = localStorage.getItem("auth");
        if (authData) {
          const parsed = JSON.parse(authData);
          parsed.user = mappedUser;
          localStorage.setItem("auth", JSON.stringify(parsed));

          // Dispatch storage event để sync với các tab khác
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: "auth",
              newValue: JSON.stringify(parsed),
              oldValue: authData,
            })
          );
        }
      } catch (error) {
      }

      setShowConfirmModal(false);
      setSelectedTransaction(null);
      setActionType(null);
      setRejectReason("");
    } catch (error: any) {
      setError(error?.message || "Có lỗi xảy ra khi thực hiện hành động");
    } finally {
      setActionLoading(null);
    }
  };

  // Xử lý tìm kiếm với debounce
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const getActionButtons = (transaction: TransactionDto) => {
    const isLoading = actionLoading === transaction.id;

    switch (transaction.status) {
      case "pending":
        return (
          <div className="action-buttons">
            <button
              className="btn-approve"
              onClick={() => handleAction(transaction, "approve")}
              disabled={isLoading}
            >
              <Check size={16} />
              {isLoading ? "Đang xử lý..." : "Duyệt"}
            </button>
            <button
              className="btn-reject"
              onClick={() => handleAction(transaction, "reject")}
              disabled={isLoading}
            >
              <X size={16} />
              {isLoading ? "Đang xử lý..." : "Từ chối"}
            </button>
          </div>
        );
      case "completed":
        return (
          <div className="action-buttons">
            <button
              className="btn-cancel"
              onClick={() => handleAction(transaction, "cancel")}
              disabled={isLoading}
            >
              <RotateCcw size={16} />
              {isLoading ? "Đang xử lý..." : "Hoàn tác"}
            </button>
          </div>
        );
      case "failed":
        return (
          <div className="action-buttons">
            <button
              className="btn-approve"
              onClick={() => handleAction(transaction, "approve")}
              disabled={isLoading}
            >
              <Check size={16} />
              {isLoading ? "Đang xử lý..." : "Duyệt"}
            </button>
            <button
              className="btn-cancel"
              onClick={() => handleAction(transaction, "cancel")}
              disabled={isLoading}
            >
              <RotateCcw size={16} />
              {isLoading ? "Đang xử lý..." : "Hủy"}
            </button>
          </div>
        );
      case "cancelled":
        return (
          <div className="action-buttons">
            <button
              className="btn-approve"
              onClick={() => handleAction(transaction, "approve")}
              disabled={isLoading}
            >
              <Check size={16} />
              {isLoading ? "Đang xử lý..." : "Duyệt"}
            </button>
            <button
              className="btn-reject"
              onClick={() => handleAction(transaction, "reject")}
              disabled={isLoading}
            >
              <X size={16} />
              {isLoading ? "Đang xử lý..." : "Từ chối"}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="history-page">
        {/* ===== HEADER ===== */}
        <div className="history-title">
          <h2>Lịch sử giao dịch của khách hàng</h2>
          <p>Danh sách các giao dịch đã được thực hiện bởi khách hàng</p>
        </div>

        {/* ===== FILTER BOX ===== */}
        <div className="history-filter-box">
          <div className="filter-label">
            <Filter size={22} className="filter-icon" />
            <span>Bộ lọc</span>
          </div>
          <div className="history-filter-action">
            <div className="search-box">
              <Search className="filter-icon" />
              <input
                type="text"
                placeholder="Tìm kiếm ngày giao dịch, mã giao dịch, email, tên người giao dịch...."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleSearch(searchQuery)
                }
              />
            </div>
            {/* Loại giao dịch */}
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setTypeOpen((v) => !v)}
              >
                {typeOptions.find((o) => o.value === typeFilter)?.label ||
                  "Loại giao dịch"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {typeOpen && (
                <div className="dropdown-menu">
                  {typeOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        typeFilter === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        setPage(1);
                        setTypeFilter(opt.value);
                        setTypeOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trạng thái */}
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setStatusOpen((v) => !v)}
              >
                {statusOptions.find((o) => o.value === statusFilter)?.label ||
                  "Trạng thái"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {statusOpen && (
                <div className="dropdown-menu">
                  {statusOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        statusFilter === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        setPage(1);
                        setStatusFilter(opt.value);
                        setStatusOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== DATA TABLE ===== */}
        <div className="history-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Mã giao dịch</th>
                <th>Trạng thái</th>
                <th>Loại</th>
                <th>Số tiền</th>
                <th>Mô tả</th>
                <th>Ngày giao dịch</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="center">
                    Đang tải...
                  </td>
                </tr>
              )}
              {error && !loading && (
                <tr>
                  <td colSpan={8} className="error">
                    {error}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                filtered.map((t, index) => {
                  const statusLabel =
                    t.status === "completed"
                      ? "Hoàn thành"
                      : t.status === "failed"
                      ? "Thất bại"
                      : t.status === "cancelled"
                      ? "Đã hủy"
                      : "Đang xử lí";
                  const statusClass =
                    t.status === "completed"
                      ? "active"
                      : t.status === "failed"
                      ? "locked"
                      : "pending";
                  return (
                    <tr key={index}>
                      <td>{t.user?.email || t.userId}</td>
                      <td>{t.transactionCode}</td>
                      <td>
                        <span className={`status ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td>{t.type}</td>
                      <td>{Number(t.amount).toLocaleString("vi-VN")}</td>
                      <td>{t.description || "-"}</td>
                      <td>{formatDateTimeWithSeconds(t.createdAt)}</td>
                      <td>{getActionButtons(t)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className="cat-pagination">
          <button
            className="btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Trước
          </button>
          <span>
            Trang {page}/{totalPages}
          </span>
          <button
            className="btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Sau
          </button>
        </div>

        {/* ===== CONFIRM MODAL ===== */}
        {showConfirmModal && selectedTransaction && actionType && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>
                {actionType === "approve" && "Xác nhận duyệt giao dịch"}
                {actionType === "reject" && "Xác nhận từ chối giao dịch"}
                {actionType === "cancel" && "Xác nhận hủy giao dịch"}
              </h3>

              <div className="modal-body">
                <p>
                  <strong>Mã giao dịch:</strong>{" "}
                  {selectedTransaction.transactionCode}
                </p>
                <p>
                  <strong>Loại:</strong> {selectedTransaction.type}
                </p>
                <p>
                  <strong>Số tiền:</strong>{" "}
                  {Number(selectedTransaction.amount).toLocaleString("vi-VN")}{" "}
                  VNĐ
                </p>
                <p>
                  <strong>Trạng thái hiện tại:</strong>{" "}
                  {selectedTransaction.status}
                </p>

                {actionType === "reject" && (
                  <div className="form-group">
                    <label>Lý do từ chối:</label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Nhập lý do từ chối giao dịch..."
                      rows={3}
                    />
                  </div>
                )}

                <div className="modal-message">
                  {actionType === "approve" &&
                    "Bạn có chắc chắn muốn duyệt giao dịch này?"}
                  {actionType === "reject" &&
                    "Bạn có chắc chắn muốn từ chối giao dịch này?"}
                  {actionType === "cancel" &&
                    "Bạn có chắc chắn muốn hủy giao dịch này?"}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedTransaction(null);
                    setActionType(null);
                    setRejectReason("");
                  }}
                >
                  Hủy
                </button>
                <button
                  className="btn-primary"
                  onClick={confirmAction}
                  disabled={actionLoading === selectedTransaction.id}
                >
                  {actionLoading === selectedTransaction.id
                    ? "Đang xử lý..."
                    : "Xác nhận"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default History;
